import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { t } from '../../util/messages';
import { withViewport } from '../../util/contextHelpers';
import { parse, stringify } from '../../util/urlHelpers';
import pickBy from 'lodash/pickBy';
import {
  Button,
  Logo,
  Modal,
  NamedLink,
  TopbarDesktop,
  TopbarMobileMenu,
} from '../../components';
import MenuIcon from './MenuIcon';
import css from './Topbar.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({
    [modalStateParam]: 'open',
    ...parse(search),
  })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (_v, k) => k !== modalStateParam);
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const TopbarComponent = props => {
  const {
    className = '',
    rootClassName = '',
    desktopClassName = '',
    mobileRootClassName = '',
    mobileClassName = '',
    viewport,
    location,
    onManageDisableScrolling,
    config,
  } = props;

  const handleMobileMenuOpen = useCallback(() => {
    redirectToURLWithModalState(props, 'mobilemenu');
  }, [props]);

  const handleMobileMenuClose = useCallback(() => {
    redirectToURLWithoutModalState(props, 'mobilemenu');
  }, [props]);

  const { mobilemenu } = parse(location.search);
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';

  const classes = classNames(rootClassName || css.root, className);

  return (
    <header className={classes}>
      {/* Mobile bar */}
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          rootClassName={css.menu}
          onClick={handleMobileMenuOpen}
          title={t('Topbar.menuIcon')}
          aria-label={t('Topbar.menuIcon')}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-haspopup="dialog"
        >
          <MenuIcon className={css.menuIcon} />
        </Button>
        <NamedLink className={css.home} name="LandingPage" aria-label="Gitribe home">
          <Logo
            format="desktop"
            width={130}
            className={css.sitelogo}
            alt={t('TopbarDesktop.logo')}
          />
        </NamedLink>
        <div className={css.spacer} />
      </div>

      {/* Desktop nav */}
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          config={config}
        />
      </div>

      {/* Mobile menu modal */}
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <TopbarMobileMenu config={config} onClose={handleMobileMenuClose} />
      </Modal>
    </header>
  );
};

const { array, func, number, shape, string, object } = PropTypes;

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  history: shape({ push: func.isRequired }).isRequired,
  location: shape({ search: string.isRequired }).isRequired,
  viewport: shape({ width: number.isRequired, height: number.isRequired }).isRequired,
  onManageDisableScrolling: func.isRequired,
  config: object,
};

const Topbar = withViewport(TopbarComponent);
Topbar.displayName = 'Topbar';

export default Topbar;
